import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { X, Clock, MapPin, Activity } from 'lucide-react';
import axios from 'axios';
import { getCountryFlag } from '../utils/geo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TraceHistory({ isOpen, onClose, onReplay }) {
  const [traces, setTraces] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchTraces();
    }
  }, [isOpen]);

  const fetchTraces = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/traces`);
      setTraces(res.data.traces || []);
    } catch (e) {
      console.error('Failed to fetch traces:', e);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <motion.div
      data-testid="open-trace-history"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="w-full max-w-lg bg-[#0a0a0a] border border-white/10 rounded-2xl shadow-2xl overflow-hidden max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-[#00f0ff]" />
            <h3 className="text-lg font-semibold text-white">Trace History</h3>
          </div>
          <button onClick={onClose} className="p-1 text-white/40 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div data-testid="trace-history-table" className="flex-1 overflow-y-auto p-4 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center h-32 text-white/30">
              <div className="w-6 h-6 border-2 border-[#00f0ff]/30 border-t-[#00f0ff] rounded-full animate-spin" />
            </div>
          ) : traces.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-32 text-white/30">
              <Activity className="w-8 h-8 mb-2" />
              <p className="text-sm">No trace history yet</p>
            </div>
          ) : (
            traces.map((trace, idx) => (
              <div
                key={trace.id || idx}
                className="bg-white/5 border border-white/10 rounded-lg p-3 hover:bg-white/8 transition-colors cursor-pointer"
                onClick={() => {
                  if (onReplay) onReplay(trace.target);
                  onClose();
                }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-4 h-4 text-[#00f0ff]" />
                    <span className="text-sm font-mono text-white/80">
                      {trace.source_city || trace.source_ip || 'Unknown'}
                    </span>
                    <span className="text-white/30">→</span>
                    <span className="text-sm font-mono text-[#00ff9d]">
                      {trace.target || 'Unknown'}
                    </span>
                  </div>
                  <span className="text-[10px] text-white/30 font-mono">
                    {trace.created_at ? new Date(trace.created_at).toLocaleDateString() : ''}
                  </span>
                </div>
                <div className="flex items-center gap-4 mt-1.5 text-xs text-white/40">
                  <span>{trace.total_hops || 0} hops</span>
                  <span>{trace.avg_latency_ms || 0}ms avg</span>
                  <span>{getCountryFlag(trace.source_country)} → {getCountryFlag(trace.target_country)}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}
